import React, { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ClipboardCheck, FileText } from '../components/icons';
import { Chip } from '../components/Chip';
import { CarteScore } from '../components/CarteScore';
import { SousScoresLinkedin } from '../components/SousScoresLinkedin';
import { useGabarit } from '../hooks/useGabarit';
import { ScreenHeader } from '../components/ScreenHeader';
import { Button } from '../components/Button';
import { GradientButton } from '../components/GradientButton';
import { OutputLanguagePicker } from '../components/OutputLanguagePicker';
import { FeedbackWidget } from '../components/FeedbackWidget';
import { LinkedinConnectionCard } from '../components/LinkedinConnectionCard';
import { LinkedinOptimizationCard } from '../components/LinkedinOptimizationCard';
import { CarteProfilLinkedin } from '../components/CarteProfilLinkedin';
import { GrilleCaptures } from '../components/GrilleCaptures';
import { PiedAction } from '../components/PiedAction';
import { Segmented } from '../components/Segmented';
import { linkedinAuditInputKey } from '../utils/resultRef';
import { palierScore } from '../utils/palierScore';
import { confirmer } from '../utils/confirmation';
import { useClaude } from '../hooks/useClaude';
import { useLinkedinConnection } from '../hooks/useLinkedinConnection';
import { MAX_IMAGES, pickImages, type PickedImage } from '../services/mediaPicker';
import { colors, formes, layout, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { FEATURE_ACCENTS } from '../theme/featureAccents';
import { OUTPUT_LANGUAGES, type LinkedinAudit, type OutputLanguage } from '../types';
import { t } from '../i18n';
import {
  fetchLinkedinAccountKit,
  fetchLinkedinKit,
  type LinkedinInput,
} from '../services/claude';
import { GenerationProgress, SECTION_MS_AUDIT } from '../components/GenerationProgress';

// Deux façons de fournir le profil : le compte LinkedIn connecté, ou des captures d'écran.
type Source = 'connect' | 'shots';

export interface LinkedinAuditScreenProps {
  onBack?: () => void;
  // Signale à la couche navigation qu'un audit non persisté est affiché (garde de sortie).
  onResultChange?: (hasResult: boolean) => void;
}

export function LinkedinAuditScreen({ onBack, onResultChange }: LinkedinAuditScreenProps = {}) {
  const { large } = useGabarit();
  const { scoreLinkedin, auditLinkedinAccount, loading, error } = useClaude();
  const connection = useLinkedinConnection();
  const [images, setImages] = useState<PickedImage[]>([]);
  const [audit, setAudit] = useState<LinkedinAudit | null>(null);
  // Langue du RÉSULTAT. Le profil source reste dans sa propre langue : on ne traduit pas ce
  // qu'on analyse, on rédige l'analyse dans la langue demandée.
  const [language, setLanguage] = useState<OutputLanguage>('fr');
  // Onglet choisi à la main ; tant qu'il est nul, on propose le compte s'il est connecté et qu'aucune
  // capture n'est déjà posée, les captures sinon.
  const [source, setSource] = useState<Source | null>(null);
  const defilement = useRef<ScrollView>(null);

  // L'audit n'est jamais persisté : quitter l'écran le détruit. Même garde que la notation de CV.
  useEffect(() => {
    onResultChange?.(audit !== null);
  }, [audit, onResultChange]);
  // Clé du résultat (feedback) dérivée de l'ENTRÉE auditée, figée quand l'audit arrive.
  const [auditKey, setAuditKey] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  /**
   * ÉTAPE 2 — le kit d'optimisation, demandé après le score.
   *
   * Son état est SÉPARÉ de celui de l'étape 1, et c'est tout l'intérêt : un échec ici ne doit
   * emporter ni le score, ni les priorités, ni le message d'erreur global. La personne garde ce
   * qu'elle a obtenu et se voit proposer une relance, plutôt que de tout recommencer — et de
   * repayer un quota, puisque l'étape 1 l'a déjà consommé.
   */
  const [etatKit, setEtatKit] = useState<'inactif' | 'encours' | 'echec'>('inactif');
  // De quoi relancer l'étape 2 à l'identique : la source et l'entrée exacte de l'étape 1.
  const [relance, setRelance] = useState<{ source: 'captures' | 'compte'; input: LinkedinInput } | null>(null);

  // Numéro de la demande de kit en cours. Une réponse qui arrive après qu'une autre demande a été
  // lancée, ou après « Relancer », est périmée : sans ça, le kit de l'audit A se grefferait sur
  // l'audit B et éteindrait son attente.
  const demandeKit = useRef(0);

  const chargerKit = async (source: 'captures' | 'compte', input: LinkedinInput) => {
    const numero = ++demandeKit.current;
    setEtatKit('encours');
    try {
      const kit =
        source === 'compte' ? await fetchLinkedinAccountKit(language) : await fetchLinkedinKit(input, language);
      if (numero !== demandeKit.current) return;
      // Le kit vient COMPLÉTER l'audit déjà affiché, il ne le remplace pas.
      setAudit((precedent) => (precedent ? { ...precedent, optimization: kit.optimization } : precedent));
      setEtatKit('inactif');
    } catch {
      if (numero !== demandeKit.current) return;
      // Volontairement silencieux vis-à-vis de `error` : ce message-là appartient à l'étape 1.
      setEtatKit('echec');
    }
  };

  // Les captures s'AJOUTENT : le sélecteur ne propose que les places restantes sur les cinq.
  const addImages = async () => {
    setLocalError(null);
    const outcome = await pickImages(MAX_IMAGES - images.length);
    if (outcome.canceled) return;
    if (outcome.error) setLocalError(outcome.error);
    setImages((prev) => [...prev, ...(outcome.images ?? [])].slice(0, MAX_IMAGES));
  };

  // L'attente s'affiche en haut de page, à la place de l'aperçu : on y remonte au lancement.
  const versLeHaut = () => defilement.current?.scrollTo?.({ y: 0, animated: true });

  const analyze = async () => {
    setLocalError(null);
    versLeHaut();
    const input: Parameters<typeof scoreLinkedin>[0] = {};
    if (images.length > 0) input.images = images.map(({ media_type, data }) => ({ media_type, data }));
    // ÉTAPE 1 : le score et les priorités seuls — une dizaine de secondes au lieu de quatre-vingt-dix.
    const result = await scoreLinkedin(input, language, 'audit');
    if (result) {
      setAudit(result);
      setAuditKey(linkedinAuditInputKey(input));
      setRelance({ source: 'captures', input });
      void chargerKit('captures', input);
    }
  };

  // Audit direct depuis le compte connecté (aucune capture). En cas d'échec, on rafraîchit l'état de
  // connexion : si le token a expiré, la carte bascule sur « Reconnecter ».
  const analyzeAccount = async () => {
    setLocalError(null);
    versLeHaut();
    const result = await auditLinkedinAccount(language, 'audit');
    if (result) {
      setAudit(result);
      setAuditKey('linkedin_account');
      setRelance({ source: 'compte', input: {} });
      void chargerKit('compte', {});
    } else {
      void connection.refresh();
    }
  };

  const sourceActive: Source = source ?? (connection.state === 'connected' && images.length === 0 ? 'connect' : 'shots');
  const analyseEnCours = loading || etatKit === 'encours';
  const surCompte = sourceActive === 'connect';
  const peutLancer = surCompte ? connection.state === 'connected' : images.length > 0;
  const shownError = localError ?? error;

  // Phrase d'état du pied : dit ce qui manque, ou que tout est prêt. Absente une fois l'audit rendu.
  const indice = (() => {
    if (audit || analyseEnCours || shownError) return null;
    if (surCompte) {
      return connection.state === 'connected'
        ? { id: 'li-hint-account', texte: t('linkedin.hint.account') }
        : { id: 'li-hint-connect', texte: t('linkedin.hint.connect') };
    }
    return images.length > 0
      ? { id: 'li-ready', texte: t('linkedin.hint.ready') }
      : { id: 'li-empty', texte: t('linkedin.hint.empty') };
  })();

  // Relancer ramène au formulaire (captures et langue conservées) mais JETTE l'audit et le kit
  // affichés : le kit coûte une longue attente et rien n'est persisté. D'où la confirmation.
  const demanderRelance = () =>
    confirmer(t('linkedin.relancerTitre'), t('linkedin.relancerMessage'), [
      { text: t('linkedin.relancerAnnuler'), style: 'cancel' },
      {
        text: t('linkedin.relancer'),
        style: 'destructive',
        onPress: () => {
          demandeKit.current += 1; // le kit encore attendu appartient à l'audit qu'on jette
          setAudit(null);
          setEtatKit('inactif');
          setRelance(null);
        },
      },
    ]);

  // Carte 15 : l'audit PREND LA PLACE du formulaire. Les sous-scores Titre / À propos / Expériences
  // (1.2.0) n'apparaissent que si le serveur les renvoie, section par section : jamais un 0 par défaut.
  if (audit) {
    const langue = OUTPUT_LANGUAGES.find((o) => o.value === language)?.label ?? language;
    const sourceAudit = relance?.source === 'compte' ? t('linkedin.connection.connected') : t('linkedin.sourceShots');
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <ScreenHeader onBack={onBack} title={t('linkedin.title')} variante="carte" testID="linkedin-back" />

        <ScrollView contentContainerStyle={styles.content}>
          <View testID="li-result" style={styles.result}>
            <CarteScore
              testID="li-carte"
              anneauTestID="li-score"
              titreTestID="li-verdict"
              detailTestID="li-score-detail"
              score={audit.score}
              titre={t(`linkedin.verdict.${palierScore(audit.score)}`)}
              detail={t('linkedin.resultDetail', { count: audit.priorities.length })}
            >
              <SousScoresLinkedin sousScores={audit.sub_scores} />
            </CarteScore>

            <View testID="li-bande" style={styles.bande}>
              <View style={styles.bandeTextes}>
                <Text style={styles.bandeTitre}>{t('linkedin.profilAnalyse')}</Text>
                <Text style={styles.bandeSous}>{`${sourceAudit} · ${langue}`}</Text>
              </View>
              <Pressable testID="li-relancer" accessibilityRole="button" onPress={demanderRelance} style={styles.relancer}>
                <Text style={styles.relancerTexte}>{t('linkedin.relancer')}</Text>
              </Pressable>
            </View>

            {audit.keywords_missing.length > 0 ? (
              <View style={styles.section}>
                <Text accessibilityRole="header" style={styles.titreSection}>
                  {t('linkedin.missingKeywords')}
                </Text>
                <View style={styles.chips}>
                  {audit.keywords_missing.map((kw, i) => (
                    <Chip key={`${kw}-${i}`} label={kw} variant="greenSoft" />
                  ))}
                </View>
              </View>
            ) : null}

            {audit.priorities.length > 0 ? (
              <View style={styles.section}>
                <Text accessibilityRole="header" style={styles.titreSection}>
                  {t('linkedin.priorities')}
                </Text>
                {audit.priorities.map((priority, index) => (
                  <View key={index} testID={`priority-${index}`} style={styles.priorite}>
                    <View testID={`priority-${index}-num`} style={styles.numero}>
                      <Text style={styles.numeroTexte}>{index + 1}</Text>
                    </View>
                    <View style={styles.prioriteCorps}>
                      <Text style={styles.priorityTitle}>{priority.title}</Text>
                      <Text style={styles.priorityWhy}>{priority.why}</Text>
                      <Text style={styles.priorityFix}>{priority.fix}</Text>
                    </View>
                  </View>
                ))}
              </View>
            ) : null}

        {/* ÉTAPE 2 : l'appel reste opaque, la temporisation demeure — mais sur les seules sections
            du kit, l'analyse ayant déjà rendu son verdict. */}
        {etatKit === 'encours' ? (
          <GenerationProgress
            messageKey="linkedin"
            sections={t('progress.linkedinKitSections') as unknown as string[]}
            testID="li-kit-progress"
          />
        ) : null}

        {/* ÉCHEC DE L'ÉTAPE 2 : le score reste à l'écran, et la relance ne coûte aucun quota —
            l'étape 1 l'a déjà consommé. */}
        {etatKit === 'echec' && relance ? (
          <View style={styles.kitFailed} testID="li-kit-failed">
            <Text style={styles.kitFailedText}>{t('linkedin.kit.failed')}</Text>
            <Button
              title={t('linkedin.kit.retry')}
              variant="secondary"
              onPress={() => void chargerKit(relance.source, relance.input)}
              testID="li-kit-retry"
            />
          </View>
        ) : null}

            {audit.optimization ? <LinkedinOptimizationCard data={audit.optimization} testID="li-optim" /> : null}

            <FeedbackWidget key={auditKey} feature="linkedin_audit" resultKey={auditKey} testID="li-feedback" />
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  const sApercu = (
    <>
        {/* ÉTAPE 1 : trois libellés à 8 s. J'avais estimé cette étape à une dizaine de secondes et
            supprimé toute séquence en conséquence ; la mesure du 07/09/2026 donne 24 s — au-dessus
            du seuil que je m'étais moi-même fixé pour justifier son absence. Le terminal apparaît à
            16 s et tient huit secondes. Elle prend la place de l'aperçu : même endroit, même taille
            d'écran, la personne n'a pas à la chercher. */}
        {loading ? (
          <GenerationProgress
            messageKey="linkedin"
            sections={t('progress.linkedinSections') as unknown as string[]}
            sectionMs={SECTION_MS_AUDIT}
            testID="li-progress"
          />
        ) : (
          <CarteProfilLinkedin
            testID="li-apercu"
            blocs={[t('linkedin.profil.titre'), t('linkedin.profil.apropos'), t('linkedin.profil.experiences')]}
          />
        )}

            </>
  );
  const sSource = (
    <>
<Segmented
          testID="li-source"
          options={[
            { key: 'connect', label: t('linkedin.sourceConnect') },
            { key: 'shots', label: t('linkedin.sourceShots') },
          ]}
          value={sourceActive}
          onChange={(k) => setSource(k as Source)}
        />

        {surCompte ? (
          // Connexion LinkedIn (OAuth), optionnelle : les captures restent la voie de repli.
          <LinkedinConnectionCard
            state={connection.state}
            busy={connection.busy}
            connect={connection.connect}
            disconnect={connection.disconnect}
            error={connection.error}
          />
        ) : (
          <View testID="li-captures-carte" style={styles.carte}>
            <Text style={styles.capturesIntro}>
              <Text style={styles.capturesGras}>{t('linkedin.capturesGras')}</Text>
              {t('linkedin.capturesSuite')}
            </Text>
            <GrilleCaptures
              testID="li-captures"
              images={images}
              max={MAX_IMAGES}
              onAdd={addImages}
              onRemove={(i) => setImages((prev) => prev.filter((_, k) => k !== i))}
              libelleAjouter={t('linkedin.addCaptures')}
              libelleRetirer={(n) => t('linkedin.captureRemove', { n })}
              ajouterTestID="li-pick"
            />
          </View>
        )}

            </>
  );
  const sLangue = (
    <>
<OutputLanguagePicker
          value={language}
          onChange={setLanguage}
          label={t('linkedin.languageLabel')}
          hint={t('linkedin.languageHint')}
          testID="li-language"
        />

            </>
  );
  const sPromesses = (
    <>
<View style={styles.promesses}>
          <View testID="li-promesse-audit" style={styles.promesse}>
            <View style={styles.promesseIcone}>
              <FileText size={19} color={FEATURE_ACCENTS.linkedin.ink} strokeWidth={2} />
            </View>
            <Text style={styles.promesseTexte}>{t('linkedin.promesses.audit')}</Text>
          </View>
          <View testID="li-promesse-kit" style={styles.promesse}>
            <View style={styles.promesseIcone}>
              <ClipboardCheck size={19} color={FEATURE_ACCENTS.linkedin.ink} strokeWidth={2} />
            </View>
            <Text style={styles.promesseTexte}>{t('linkedin.promesses.kit')}</Text>
          </View>
        </View>

    </>
  );
  const sPied = (
    <>
      {/* Le pied reste visible : un échec ou la phrase d'état s'y affichent, à côté du bouton qui
          les provoque, et non plus bas dans une page qu'il faudrait défiler. */}
      <PiedAction testID="li-actions" enColonne={large}>
        {/* Quota → le Paywall s'ouvre (déclenché dans useClaude), pas de message inline. */}
        {shownError ? (
          <Text testID="li-error" style={styles.erreur}>
            {shownError}
          </Text>
        ) : null}
        {indice ? (
          <Text testID={indice.id} style={styles.indice}>
            {indice.texte}
          </Text>
        ) : null}

        <GradientButton
          variante="cta"
          hauteur="lg"
          label={t('linkedin.analyse')}
          onPress={surCompte ? analyzeAccount : analyze}
          loading={loading}
          disabled={!peutLancer}
          withArrow
          testID={surCompte ? 'li-analyze-account' : 'li-submit'}
        />
      </PiedAction>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader
        onBack={onBack}
        title={t('linkedin.title')}
        sousTitre={t('linkedin.subtitle')}
        variante="carte"
        testID="linkedin-back"
      />

              {large ? (
          <ScrollView contentContainerStyle={styles.colonnesL} showsVerticalScrollIndicator={false}>
            <View style={styles.colGaucheL}>
              {sApercu}
              {sPromesses}
            </View>
            <View style={styles.colDroiteL}>
              {sSource}
              {sLangue}
              <View style={styles.piedL}>{sPied}</View>
            </View>
          </ScrollView>
        ) : (
          <ScrollView ref={defilement} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
            {sApercu}
            {sSource}
            {sLangue}
            {sPromesses}
          </ScrollView>
        )}
      {large ? null : sPied}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  colonnesL: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheL: { flex: 1, minWidth: 0, gap: spacing.lg },
  colDroiteL: { flex: 1, minWidth: 0, gap: spacing.md },
  piedL: { marginTop: spacing.sm },
  // Encart d'échec partiel : teinté, jamais rouge vif. Rien n'est perdu, seule la seconde moitié
  // manque — le ton doit dire « à réessayer », pas « c'est cassé ».
  kitFailed: {
    gap: spacing.md,
    padding: spacing.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.grey50,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  kitFailedText: {
    fontSize: typography.small.size,
    lineHeight: typography.small.lineHeight,
    color: colors.grey700,
  },
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, paddingTop: spacing.sm, gap: spacing.md },

  carte: { gap: spacing.md, padding: spacing.lg, borderRadius: formes.rayon.lg, backgroundColor: colors.white, ...shadow.card },
  capturesIntro: { fontSize: 13, lineHeight: 17, color: colors.grey500 },
  capturesGras: { fontWeight: '800', color: colors.night },

  promesses: { flexDirection: 'row', gap: spacing.sm + 2 },
  promesse: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm + 2,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  promesseIcone: {
    width: 38,
    height: 38,
    borderRadius: formes.rayon.sm,
    backgroundColor: FEATURE_ACCENTS.linkedin.tint,
    alignItems: 'center',
    justifyContent: 'center',
  },
  promesseTexte: { flex: 1, fontSize: 13.5, lineHeight: 17, fontWeight: '800', color: colors.night },

  indice: { textAlign: 'center', fontSize: 12.5, color: colors.grey500 },
  erreur: { textAlign: 'center', fontSize: typography.small.size, color: colors.error },

  result: { gap: spacing.md, marginTop: spacing.sm },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },

  // Bande « Profil analysé » : ce qui a été lu, et la porte pour recommencer.
  bande: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  bandeTextes: { flex: 1, minWidth: 0, gap: 2 },
  bandeTitre: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  bandeSous: { fontSize: typography.small.size, color: colors.grey500 },
  relancer: {
    minHeight: formes.cible,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: FEATURE_ACCENTS.linkedin.tint,
  },
  relancerTexte: { fontSize: typography.small.size, fontWeight: '800', color: FEATURE_ACCENTS.linkedin.ink },

  section: { gap: spacing.sm },
  titreSection: {
    fontSize: typography.h3.size,
    lineHeight: typography.h3.lineHeight,
    fontWeight: typography.h3.weight,
    color: colors.night,
  },
  priorite: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  numero: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: FEATURE_ACCENTS.linkedin.tint,
  },
  numeroTexte: { fontSize: 13, fontWeight: '800', color: FEATURE_ACCENTS.linkedin.ink },
  // `flex: 1` + `minWidth: 0` : un texte long passe à la ligne au lieu de sortir de la carte.
  prioriteCorps: { flex: 1, minWidth: 0 },
  priorityTitle: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.grey900 },
  priorityWhy: { marginTop: spacing.xs, fontSize: typography.small.size, color: colors.grey500 },
  priorityFix: { marginTop: spacing.xs, fontSize: typography.body.size, color: colors.grey700 },
});
